import { SapAssignedPerson, TableComponentState } from 'app/models/reducers/component-store';
import {
  EditOrderCalculationData,
  OrderTypeCalculationData,
  SapConfirmFullOrderOperationForm,
  SapCreateNotificationForm,
  SapCreateOrderFromNotificationVM,
  SapCreateOrderOperationVM,
  SapCreateOrderWithoutNotificationForm,
  SapLoadedDataType,
  SapNotificationDetailsForm,
  SapNotificationsFilter,
  SapNotificationVM,
  SapOrderDetailsEditForm,
  SapOrderOperationVM,
  SapOrdersFilter,
  SapOrderVM,
  SapUpdateOrderOperationVM
} from 'app/models/sap';
import { MetaData } from 'app/models/reducers/meta-data';
import { DateRange } from 'app/models/date-range';
import { MachineBriefData } from 'app/models/api/machines';
import { MetaDataReducerUtils } from './meta-data.utils';
import { convertToDateRange } from '../date';
import { SettingsSapUser, UserSettings } from 'app/models/api/user-settings';
import {
  ArrayUtils,
  AssetNodeType,
  AssignPersonsToOperationModel,
  CreateMaintenanceNotificationModel,
  CreateMaintenanceOrder,
  CreateMaintenanceOrderFromNotificationModel,
  CreateMaintenanceOrderOperationConfirmationModel,
  CreateMaintenanceOrderOperationModel,
  EquipmentOverviewResponseDto,
  EquipmentsOverviewRequestDto,
  FunctionalLocation,
  GetAllEquipmentsOverviewOrderByType,
  GetAllMaintenanceOrderOperationsFilterOptionsDto,
  GetPersonsAssignedToWorkcenterFilterOptionsDto,
  MaintenanceNotification,
  MaintenanceOrderAndOperation,
  NotificationType,
  PersonAssignedToOperationDto,
  PersonAssignedToWorkcenterDto,
  SapActivityTypeDto,
  SapOrder,
  SapOrderDirectionType,
  SapOrderDto,
  SapOrderingFilterOption,
  SapOrderTypeDto,
  SapPagination,
  SapPaginationFilterOption,
  SapPriorityType,
  SapSystemStatusDto,
  SapUserStatusDto,
  SapWorkCenterDto,
  SetOrderAsTechnicallyCompleteModel,
  SharedSapUtils,
  StringUtils,
  UpdateMaintenanceNotificationModel,
  UpdateMaintenanceOrderModel,
  UpdateMaintenanceOrderOperationModel,
  WebPubSubRequest,
  WebPubSubResponse,
  WebPubSubTopicRequestResponse
} from '@festo-ax/smartenance-shared-ui';
import { SapMachinesOverviewFilter } from '../../models/sap';
import { SapTypesMapperUtils } from './sap-types-mapper.utils';
import { SelectedSapAsset } from '../../models/reducers/sap-assets';

export class SapUtils {
  static mergeRequestWithLanguageFromUserSettings<K extends keyof WebPubSubTopicRequestResponse>(
    request: WebPubSubRequest<K>,
    userSettings: UserSettings | null,
    fallbackLanguage: string
  ): WebPubSubRequest<K> {
    return { ...request, data: { ...request.data, language: userSettings?.sapLanguage || fallbackLanguage } };
  }

  static convertStatusBack(status: SapUserStatusDto[]): string | null {
    if (ArrayUtils.isEmpty(status)) {
      return null;
    }

    return status.map(u => u.status).join(' ');
  }

  static convertStatus(value: string | undefined): string[] {
    if (!StringUtils.isString(value) || value.length === 0) {
      return [];
    }

    return ArrayUtils.removeEmpty(value.split(' '));
  }

  static mapMaintenanceNotificationsToSapNotificationsVM(dtos: MaintenanceNotification[]): SapNotificationVM[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(dto => this.mapMaintenanceNotificationToSapNotificationVM(dto));
  }

  static mapMaintenanceNotificationToSapNotificationVM(dto: MaintenanceNotification): SapNotificationVM {
    return {
      notificationId: dto?.MaintenanceNotification || null,
      title: dto?.NotificationText || null,
      priority: SapTypesMapperUtils.convertStringToPriority(dto?.MaintPriority),
      isMachineDown: !!dto?.MaintenanceObjectIsDown,
      description: dto?.MaintNotificationLongText || null,
      orderId: dto?.MaintenanceOrder || null,
      isAvailableToCreateOrder: !!dto?.IsAvailableToCreateOrder,
      notificationSystemState: this.convertStatus(dto?.ConcatenatedActiveSystStsName),
      processingPhase: {
        code: dto?.NotifProcessingPhase || null,
        description: dto?.NotifProcessingPhaseDesc || null
      },
      notificationUserState: this.convertStatus(dto?.YConcatenatedActiveUserStsName),
      machine: {
        number: dto?.TechnicalObject || null,
        label: dto?.TechnicalObjectDescription || dto?.TechnicalObjectLabel || null
      },
      isEditable: !!dto?.IsEditable,
      failureType: {
        code: dto?.MaintNotificationCode || null,
        description: dto?.MaintNotifCodingCtlgCodeText || null,
        catalog: dto?.MaintNotificationCatalog || null,
        // default language here, if value will be change it will be taken from response that have an updated value
        // just to complete the interface, this value does not affect on data
        language: 'DE',
        codeGroup: dto?.MaintNotificationCodeGroup || null
      },
      workCenter: {
        code: dto?.MainWorkCenter || null,
        description: dto?.MainWorkCenterText || null,
        workCenterPlant: dto?.MainWorkCenterPlant || null
      },
      functionalLocation: {
        code: dto?.FunctionalLocation || null,
        description: dto?.FunctionalLocationLabelName || null
      },
      plannerGroup: {
        code: dto?.MaintenancePlannerGroup || null,
        description: dto?.MaintenancePlannerGroupName || null
      },
      notificationType: {
        code: dto?.NotificationType || null,
        description: dto?.NotificationTypeLabel || null,
        catalog: dto?.MaintNotificationCatalog,
        catalogProfile: dto?.CatalogProfile,
      },
      reportedBy: StringUtils.takeNameFromEmail(dto?.ReportedByUser),
      plant: dto?.MaintenancePlanningPlant || null,
      assetLocation: dto?.AssetLocation || null,
      creationDate: SapTypesMapperUtils.convertSapDateStringToDate(dto?.NotificationCreationDate),
      creationTime: SapTypesMapperUtils.convertSapTimeStringToDate(dto?.NotificationCreationTime),
      malfunctionStartDate: SapTypesMapperUtils.convertSapDateStringToDate(dto?.MalfunctionStartDate),
      malfunctionStartTime: SapTypesMapperUtils.convertSapTimeStringToDate(dto?.MalfunctionStartTime),
      malfunctionEndDate: SapTypesMapperUtils.convertSapDateStringToDate(dto?.MalfunctionEndDate),
      malfunctionEndTime: SapTypesMapperUtils.convertSapTimeStringToDate(dto?.MalfunctionEndTime),
      objectType: dto?.TechObjIsEquipOrFuncnlLoc || null
    };
  }

  static mapMaintenanceOrderAndOperationToSapOrdersVM(dto: MaintenanceOrderAndOperation[]): SapOrderVM[] {
    if (ArrayUtils.isEmpty(dto)) {
      return [];
    }

    return dto.map(this.mapMaintenanceOrderAndOperationToSapOrderVM.bind(this));
  }

  /**
   * @deprecated, should be deleted after completing of sap migration
   */
  static mapMaintenanceOrderAndOperationToSapOrderVMDeprecated(dto: MaintenanceOrderAndOperation): SapOrderVM {
    return {
      orderId: dto?.MaintenanceOrder || null,
      priority: SapTypesMapperUtils.convertStringToPriority(dto?.MaintPriority),
      orderSystemState: this.convertStatus(dto?.SystemStatusText),
      orderUserState: this.convertStatus(dto?.UserStatusText),
      title: dto?.MaintenanceOrderTitle || null,
      description: dto?.MaintenanceOrderDesc || null,
      machine: {
        number: dto?.TechnicalObject || null,
        label: dto?.TechnicalObjectLabel || null
      },
      startDate: SapTypesMapperUtils.convertSapDateStringToDate(dto.MaintOrdBasicStartDateTime),
      endDate: SapTypesMapperUtils.convertSapDateStringToDate(dto.MaintOrdBasicEndDateTime),
      notificationId: dto?.MaintenanceNotification || null,
      orderType: {
        code: dto?.MaintenanceOrderType || null,
        description: null // TODO we do not have any description for order type
      },
      reportedBy: dto?.ReportedBy || null,
      activityType: {
        code: dto?.ActivityType || null,
        description: dto?.ActivityTypeText || null
      },
      plannerGroup: {
        code: dto?.MaintenancePlannerGroup || null,
        description: dto?.MaintenancePlannerGroupName || null
      },
      workCenter: {
        code: dto?.MainWorkCenter || null,
        description: dto?.MainWorkCenterText || null
      },
      assetLocation: dto?.AssetLocation || null,
      plant: dto?.Plant || null,
      functionalLocation: {
        code: dto?.FunctionalLocation || null,
        description: dto?.FunctionalLocationDescription || null
      },
      objectNumber: dto?.ObjectNumber || null,
      isTechnicallyCompleted: dto?.IsTechnicallyCompleted,
      isAllOperationCompleted: dto?.IsFinallyConfirmed,
    };
  }

  static mapMaintenanceOrderAndOperationToSapOrderVM(dto: SapOrderDto): SapOrderVM {
    return {
      orderId: dto?.orderId || null,
      priority: SapTypesMapperUtils.convertStringToPriority(dto?.priority),
      orderSystemState: SapTypesMapperUtils.convertStatusDto(dto?.systemStatus),
      orderUserState: SapTypesMapperUtils.convertStatusDto(dto?.userStatus),
      title: dto?.title || null,
      description: dto?.description || null,
      machine: {
        number: dto?.machine?.number || null,
        label: dto?.machine?.label || null
      },
      startDate: SapTypesMapperUtils.convertSapDateStringToDate(dto.startDate),
      endDate: SapTypesMapperUtils.convertSapDateStringToDate(dto.endDate),
      notificationId: dto?.notificationId || null,
      orderType: {
        code: dto?.orderType?.code || null,
        description: dto?.orderType?.description || null
      },
      reportedBy: dto?.reportedBy || null,
      activityType: {
        code: dto?.activityType?.code || null,
        description: dto?.activityType?.description || null
      },
      plannerGroup: {
        code: dto?.plannerGroup?.code || null,
        description: dto?.plannerGroup?.description || null
      },
      workCenter: {
        code: dto?.workCenter?.code || null,
        description: dto?.workCenter?.description || null
      },
      assetLocation: dto?.assetLocation || null,
      plant: dto?.plant || null,
      functionalLocation: {
        code: dto?.functionalLocation?.code || null,
        description: dto?.functionalLocation?.description || null
      },
      objectNumber: dto?.objectNumber || null,
      isTechnicallyCompleted: dto?.isTechnicallyCompleted,
      isAllOperationCompleted: dto?.isFinallyConfirmed,
    };
  }

  // TODO refactor later
  static mapNotificationsResponseToLocaleState(
    state: TableComponentState<SapNotificationVM, SapNotificationsFilter, unknown>,
    response: WebPubSubResponse<'getAllMaintenanceNotifications'>
  ): TableComponentState<SapNotificationVM, SapNotificationsFilter, unknown> {
    const data: SapNotificationVM[] = this.mapMaintenanceNotificationsToSapNotificationsVM(response.data?.maintenanceNotifications);

    const totalAmount: number = response.data?.totalAmount || 0;
    const offset: number = response.data?.offset || 0;

    const meta: MetaData = MetaDataReducerUtils.updatePagination(state.meta, totalAmount, offset);

    return { ...state, data, meta };
  }

  // TODO should be all orders here
  // static mapOrdersResponseToLocaleState(
  //   state: TableComponentState<SapOrderVM, SapOrdersFilter, unknown>,
  //   response: WebPubSubResponse<'getAllMaintenanceOrders'>
  // ): TableComponentState<SapOrderVM, SapOrdersFilter, unknown> {
  //   const data: SapOrderVM[] = ArrayUtils.isNotEmpty(response.data?.result?.items)
  //     ? this.mapMaintenanceOrderAndOperationToSapOrdersVM(response.data.result?.items)
  //     : [];
  //
  //   const totalAmount: number = response.data?.result?.totalAmount || 0;
  //   const offset: number = response.data?.result?.offset || 0;
  //
  //   const meta: MetaData = MetaDataReducerUtils.updatePagination(state.meta, totalAmount, offset);
  //
  //   return { ...state, data, meta };
  // }

  static mapMaintenanceNotificationToFormDetails(notification: SapNotificationVM): SapNotificationDetailsForm {
    return {
      machineIsDownStatus: !!notification?.isMachineDown,
      priority: notification?.priority || '',
      equipment: notification?.machine.number || '',
      location: notification?.assetLocation || '', // read only value
      title: notification?.title || '',
      description: '', // we do not provide any value here cuz changes will be prepended to previous description
      systemStatus: notification?.notificationSystemState, // read only value,
      userStatus: notification?.notificationUserState, // read only value,
      technicalObject: notification?.functionalLocation.code || '', // read only value
      failureType: { ...notification.failureType }
    };
  }

  static mapFormDetailsToUpdateNotificationModel(
    details: SapNotificationDetailsForm,
    notification: SapNotificationVM,
    reportedBy: string
  ): UpdateMaintenanceNotificationModel {
    return {
      NotificationType: notification.notificationType.code,
      TechObjIsEquipOrFuncnlLoc: notification.objectType,
      MainWorkCenterPlant: notification.workCenter.workCenterPlant,
      ReportedByUser: reportedBy,
      MaintenanceObjectIsDown: details.machineIsDownStatus,
      // data provided from form
      MaintPriority: details.priority as SapPriorityType,
      TechnicalObject: details.equipment,
      NotificationText: details.title,
      MaintNotifLongTextForEdit: details.description,
      MaintNotificationCode: details.failureType.code,
      MaintNotificationCodeGroup: notification.failureType.codeGroup
    };
  }

  static createUpdateNotificationRequest(
    updateModel: UpdateMaintenanceNotificationModel,
    maintenanceNotificationId: string,
    language: string
  ): WebPubSubRequest<'updateMaintenanceNotification'> {
    return {
      topic: 'updateMaintenanceNotification',
      data: {
        language,
        maintenanceNotificationId,
        maintenanceNotification: updateModel
      }
    };
  }

  static mapCreateNotificationFormToCreateModel(
    details: SapCreateNotificationForm,
    reportedBy: string,
    machineNumber: string | null
  ): CreateMaintenanceNotificationModel {
    return {
      ReportedByUser: StringUtils.takeNameFromEmail(reportedBy).slice(0, 11),
      TechObjIsEquipOrFuncnlLoc: 'EAMS_EQUI',
      NotificationText: details.title,
      NotificationType: details.notificationTypes.NotificationType,
      TechnicalObject: machineNumber ?? details.machine,
      MaintPriority: details.priority as SapPriorityType,
      MaintenanceObjectIsDown: details.machineIsDownStatus,
      RequiredStartDate: SapUtils.dateToSapDate(details.occurredAtDate, details.occurredAtTime),
      RequiredStartTime: SapUtils.timeToSapTime(details.occurredAtTime),
      MaintNotifLongTextForEdit: details.description,
      MaintNotificationCode: details.failureType.code,
      MaintNotificationCodeGroup: details.failureType.codeGroup,
    };
  }

  static mapCreateOrderFormToCreateModel(
    details: SapCreateOrderWithoutNotificationForm,
    reportedBy: string,
    machineNumber: string | null
  ): CreateMaintenanceOrder {
    return {
      ShortText: details.title,
      Longtext: details?.description ?? '',
      OrderType: details.orderTypes.code,
      MnWkCtr: details.workCenter.code,
      MnWkPlant: details.workCenter.plant,
      Plangroup: details.plannerGroup.code,
      Planplant: details.plannerGroup.plant,
      Pmacttype: details.activityType.code,
      Equipment: machineNumber ?? details.machine,
      Priority: details.priority
    };
  }

  static mapSapOrderVMToEditForm(order: SapOrderVM): SapOrderDetailsEditForm {
    return {
      title: order.title,
      priority: order.priority,
      userStatus: order.orderUserState,
      activityType: {
        code: order.activityType.code,
        id: order.activityType.code,
        description: order.activityType.description,
        orderType: null // we do not use this value during editing and during creation request for update so just provide null
      },
      workCenter: {
        code: order.workCenter.code,
        description: order.workCenter.description,
        plant: null // we do not use this value during editing and during creation request for update so just provide null
      },
      planerGroup: {
        code: order.plannerGroup.code,
        description: order.plannerGroup.description,
        plant: null // we do not use this value during editing and during creation request for update so just provide null
      },
      description: order.description,
      notificationNumber: order.notificationId,
      orderNumber: order.orderId,
      systemStatus: order.orderSystemState,
      reportedBy: order.reportedBy,
      basicStart: order.startDate,
      basicEnd: order.endDate,
      orderType: {
        code: order.orderType.code,
        description: order.orderType.description,
        // we do not use these values during editing and during creation request for update so just provide null and []
        activityTypes: [],
        defaultActivityType: null
      }
    }
  }

  static mapCreateOrderFromNotificationVmToRequestModel(
    dto: SapCreateOrderFromNotificationVM
  ): CreateMaintenanceOrderFromNotificationModel {
    return {
      NotifNo: dto.notificationId,
      OrderType: dto.orderType.code,
      MnWkCtr: dto.workCenter.code,
      MnWkPlant: dto.workCenter.plant,
      Plangroup: dto.plannerGroup.code,
      Pmacttype: dto.activityType.code
    };
  }

  static mapSapCreateOrderOperationVMToRequestModel(
    dto: SapCreateOrderOperationVM
  ): CreateMaintenanceOrderOperationModel {
    return {
      MaintenanceOrderId: dto.orderId,
      MaintenanceOrderTitle: dto.description,
      MaintenanceOrderDesc: dto.comments || '',
      MainWorkCenter: dto.workCenter.code,
      Plant: dto.workCenter.plant,
      AssignedPersons: ArrayUtils.isNotEmpty(dto.sapUsers) ? dto.sapUsers.map(a => a.personId) : [],
      UserStatus: this.convertStatusBack(dto.userStatus)
    };
  }

  static mapSapUpdateOrderOperationVMToRequestModel(
    dto: SapUpdateOrderOperationVM
  ): UpdateMaintenanceOrderOperationModel {
    return {
      MaintenanceOrderId: dto.orderId,
      MaintenanceOrderOperationId: dto.operationId,
      MaintenanceOrderTitle: dto.description,
      MaintenanceOrderDesc: dto.comments || '',
      MainWorkCenter: dto.workCenter.code,
      ObjectNumber: dto.objectNumber,
      UserStatus: this.convertStatusBack(dto.userStatus),
      AssignedPersons: ArrayUtils.isNotEmpty(dto.sapUsers) ? dto.sapUsers.map(a => a.personId) : [],
    };
  }

  static mapSapConfirmOperationVMToRequestModel(
    dto: SapConfirmFullOrderOperationForm & { orderId: string }
  ): CreateMaintenanceOrderOperationConfirmationModel {
    return {
      MaintenanceOrder: dto.orderId,
      MaintenanceOrderOperation: dto.operationId,
      IsFinalConfirmation: dto.isSuccessful,
      MaintOrderConfLongText: dto.comments,
      ActualWorkQuantity: SharedSapUtils.minutesToSapHours(dto.actualWorkHours * 60, dto.actualWorkMinutes),
    };
  }

  static mapSapOrderVmToSetOrderAsTechnicallyCompleteModel(dto: SapOrderVM): SetOrderAsTechnicallyCompleteModel {
    return {
      OrderId: dto.orderId,
      BusinessCompletion: ''
    }
  }

  static mapOrderEditFormToUpdateMaintenanceOrderModel(
    data: SapOrderDetailsEditForm, ObjectNumber: string|null
  ): UpdateMaintenanceOrderModel {
    return {
      ObjectNumber,
      MaintenanceOrderId: data.orderNumber,
      MaintenanceOrderTitle: data.title,
      MaintenanceOrderDesc: data.description,
      MaintPriority: data.priority,
      Username: data.reportedBy,
      MaintenancePlannerGroup: data.planerGroup.code,
      UserStatus: data.userStatus ? data.userStatus.join(' ') : '',
      MainWorkCenter: data.workCenter.code,
      ActivityType: data.activityType.code
    }
  }

  static createAssignPersonsToOperationModel(orderId: string, operation: SapOrderOperationVM, settingsUser: SettingsSapUser): AssignPersonsToOperationModel {
    return {
      MaintenanceOrderId: orderId,
      MaintenanceOrderOperationId: operation.operationId,
      AssignedPersons: [
        ...(ArrayUtils.isNotEmpty(operation.assignedPerson) ? operation.assignedPerson.map(a => a.personId) : []),
        settingsUser.id
      ]
    }
  }

  static createCreateNotificationRequest(
    createModel: CreateMaintenanceNotificationModel,
    language: string
  ): WebPubSubRequest<'createMaintenanceNotification'> {
    return {
      topic: 'createMaintenanceNotification',
      data: {
        language,
        maintenanceNotification: createModel
      }
    };
  }

  static createUpdateMaintenanceOrderRequest(
    createModel: CreateMaintenanceOrder,
    language: string
  ): WebPubSubRequest<'createMaintenanceOrder'> {
    return {
      topic: 'createMaintenanceOrder',
      data: {
        language,
        maintenanceOrder: createModel
      }
    };
  }

  static createCreateOrderFromNotificationRequest(
    createModel: CreateMaintenanceOrderFromNotificationModel,
    language: string
  ): WebPubSubRequest<'createMaintenanceOrderFromNotification'> {
    return {
      topic: 'createMaintenanceOrderFromNotification',
      data: {
        language,
        maintenanceOrderFromNotification: createModel
      }
    };
  }

  static createCreateOrderOperationRequest(
    createModel: CreateMaintenanceOrderOperationModel,
    language: string
  ): WebPubSubRequest<'createMaintenanceOrderOperation'> {
    return {
      topic: 'createMaintenanceOrderOperation',
      data: {
        language,
        maintenanceOrderOperation: createModel
      }
    };
  }

  static createUpdateOrderOperationRequest(
    createModel: UpdateMaintenanceOrderOperationModel,
    language: string
  ): WebPubSubRequest<'updateMaintenanceOrderOperation'> {
    return {
      topic: 'updateMaintenanceOrderOperation',
      data: {
        language,
        maintenanceOrderOperation: createModel
      }
    };
  }

  static createConfirmOrderOperationRequest(
    createModel: CreateMaintenanceOrderOperationConfirmationModel,
    language: string
  ): WebPubSubRequest<'createMaintenanceOrderOperationConfirmation'> {
    return {
      topic: 'createMaintenanceOrderOperationConfirmation',
      data: {
        language,
        orderOperationConfirmation: createModel
      }
    };
  }

  static createTakeOverOrderOperationRequest(
    createModel: AssignPersonsToOperationModel,
    language: string
  ): WebPubSubRequest<'assignPersonsToOperation'> {
    return {
      topic: 'assignPersonsToOperation',
      data: {
        language,
        assignPersonsToOperationModel: createModel
      }
    };
  }

  static mapMachineNames(machines: MachineBriefData[]): MachineBriefData[] {
    if (ArrayUtils.isEmpty(machines)) {
      return [];
    }

    return machines.map(this.mapMachineName.bind(this));
  }

  private static mapMachineName(machine: MachineBriefData): MachineBriefData {
    return {
      name: machine.name,
      number: machine.number.padStart(18, '0'),
      id: machine.id
    };
  }

  static createRequestForSapNotificationDetails(
    maintenanceNotificationId: string,
    language: string = 'en'
  ): WebPubSubRequest<'getMaintenanceNotification'> {
    return {
      topic: 'getMaintenanceNotification',
      data: {
        language,
        maintenanceNotificationId
      }
    };
  }

  static createRequestForSapOrderDetails(orderId: string, language: string = 'en'): WebPubSubRequest<'getMaintenanceOrder'> {
    return {
      topic: 'getMaintenanceOrder',
      data: {
        language,
        id: orderId
      }
    };
  }

  static createRequestForTechnicallyCompleteSapOrder(model: SetOrderAsTechnicallyCompleteModel, language: string = 'en'): WebPubSubRequest<'setOrderAsTechnicallyComplete'> {
    return {
      topic: 'setOrderAsTechnicallyComplete',
      data: {
        language,
        setOrderAsTechnicallyCompleteModel: model
      }
    };
  }

  static createRequestForSapOrderUpdateDetails(model: UpdateMaintenanceOrderModel, language: string = 'en'): WebPubSubRequest<'updateMaintenanceOrder'> {
    return {
      topic: 'updateMaintenanceOrder',
      data: {
        language,
        maintenanceOrder: model
      }
    };
  }

  /**
   * @deprecated
   */
  static createTableOrderOldFilter(meta: MetaData): SapOrder {
    return {
      OrderBy: meta.order.orderBy,
      OrderDirection: meta.order.ascending ? 'asc' : 'desc'
    };
  }

  static createTableOrderFilter<T extends string>(meta: MetaData): SapOrderingFilterOption<T> {
    return {
      orderBy: meta.order.orderBy as T, // we need brand-new interface Meta with generic Order to provide this data correctly
      orderDirection: meta.order.ascending ? 'asc' : 'desc'
    };
  }

  /**
   * @deprecated
   */
  static createTablePaginationDeprecatedFilter(meta: MetaData): SapPagination {
    const {
      paginationOptions: { count, page }
    } = meta;

    return { Top: count, Skip: count * (page - 1) };
  }


  static createTablePaginationFilter(meta: MetaData): SapPaginationFilterOption {
    const {
      paginationOptions: { count, page }
    } = meta;

    return { top: count, skip: count * (page - 1) };
  }

  static createRequestForSapOrderOperations(
    maintenanceOrderId: string,
    sapMachineId: string,
    meta: MetaData,
    language: string = 'en',
    loadType?: SapLoadedDataType
  ): WebPubSubRequest<'getAllMaintenanceOrderOperations'> {
    return {
      topic: 'getAllMaintenanceOrderOperations',
      data: {
        language,
        filterOptions: {
          equipment: [sapMachineId],
          maintenanceOrder: [maintenanceOrderId],
          // table filters
          ...this.createTableOrderFilter(meta),
          ...this.createTablePaginationFilter(meta),
          // refresh flag
          ...(loadType && { freshDataFlag: loadType.refreshData }),

        }
      }
    };
  }

  static createFilterOptionsForSapMachinesRequest(meta: MetaData, filters: SapMachinesOverviewFilter): EquipmentsOverviewRequestDto {
    // Filtering
    const filter: string|null = filters?.search || null;
    const isInactive: boolean|null = filters?.inactive != null ? filters.inactive : null;
    const isInstalled: boolean|null = filters?.installed != null ? filters.installed : null;
    const isMarkedForDeletion: boolean|null = filters?.markForDeleted != null ? filters.markForDeleted : null;
    const workCenters: string[]|null = this.mapWorkcentersForRequest(filters.workcenters);
    const functionalLocations: string[]|null = this.mapLocationsForRequest(filters.locations);

    // Pagination & ordering
    const { paginationOptions: { count, page } }: MetaData = meta;
    const offset: number = (page - 1) * count;
    const orderBy: GetAllEquipmentsOverviewOrderByType = meta.order.orderBy as GetAllEquipmentsOverviewOrderByType;
    const orderDirection: SapOrderDirectionType = meta.order.ascending ? 'Asc' : 'Desc';

    return {
      isInactive,
      isInstalled,
      isMarkedForDeletion,
      ...(filter ? { filter } : {}),
      ...(workCenters ? { workCenters } : {}),
      ...(functionalLocations ? { functionalLocations } : {}),
      count,
      offset,
      orderBy,
      orderDirection,
    }
  }

  static createRequestForSapMachines(
    meta: MetaData,
    filters: SapMachinesOverviewFilter,
    language: string = 'en'
  ): WebPubSubRequest<'getAllEquipmentsOverview'> {
    const filterOptions: EquipmentsOverviewRequestDto = this.createFilterOptionsForSapMachinesRequest(meta, filters);

    return {
      topic: 'getAllEquipmentsOverview',
      data: { language, filterOptions },
    }
  }

  static createRequestForSapMachinesForAssets(
    meta: MetaData,
    selected: SelectedSapAsset,
    language: string = 'en'
  ): WebPubSubRequest<'getAllEquipmentsOverview'> {
    // Filtering
    const functionalLocations: string[]|null = selected.nodeType === AssetNodeType.GROUP && selected.nodeId !== 'root'
      ? [selected.nodeId]
      : [];

    // Pagination & ordering
    const { paginationOptions: { count, page } }: MetaData = meta;
    const offset: number = (page - 1) * count;
    const orderBy: GetAllEquipmentsOverviewOrderByType = meta.order.orderBy as GetAllEquipmentsOverviewOrderByType;
    const orderDirection: SapOrderDirectionType = meta.order.ascending ? 'Asc' : 'Desc';

    return {
      topic: 'getAllEquipmentsOverview',
      data: {
        language,
        filterOptions: {
          functionalLocations,
          count,
          offset,
          orderBy,
          orderDirection,
        }
      },
    }
  }

  static createFilterOptionsForAsyncSapMachinesRequest(searchText: string): EquipmentsOverviewRequestDto {
    // Filtering
    const filter: string|null = searchText;

    // Ordering
    const orderBy: GetAllEquipmentsOverviewOrderByType = "Name" as GetAllEquipmentsOverviewOrderByType;
    const orderDirection: SapOrderDirectionType = 'Desc' as SapOrderDirectionType;

    return {
      ...(filter && { filter }),
      count: 25,
      orderBy,
      orderDirection,
    }
  }

  static createRequestForAsyncSapMachines(
    searchText: string = '',
    language: string = 'en'
  ): WebPubSubRequest<'getAllEquipmentsOverview'> {
    const filterOptions: EquipmentsOverviewRequestDto = this.createFilterOptionsForAsyncSapMachinesRequest(searchText);

    return {
      topic: 'getAllEquipmentsOverview',
      data: { language, filterOptions },
    }
  }

  static createRequestForSapNotifications(
    meta: MetaData,
    filters: SapNotificationsFilter,
    FreshDataFlag: boolean,
    language: string = 'en',
    machineNumber: string | null = null
  ): WebPubSubRequest<'getAllMaintenanceNotifications'> {

    const search: string|null = filters?.search || null;

    const systemStatuses: string[]|null = this.mapStatusItemForRequest(filters.systemStates);
    const workcenters: string[]|null = this.mapWorkcentersForRequest(filters.workcenters);
    const locations: string[]|null = this.mapLocationsForRequest(filters.locations);
    const notificationTypes: string[]|null = this.mapNotificationTypesForRequest(filters.notificationTypes);
    const sapMachines: string[]|null = this.mapSapMachinesForRequest(filters.sapAsyncMachines, machineNumber);

    const timestampRange: DateRange = convertToDateRange(filters.timestampRange);
    const malfunctionRange: DateRange = convertToDateRange(filters.malfunctionRange);
    const timestampStart: string | null = timestampRange.disabled ? null : this.getDateTimeString(timestampRange.from);
    const timestampEnd: string | null = timestampRange.disabled ? null : this.getDateTimeString(timestampRange.to);
    const malfunctionStart: string | null = malfunctionRange.disabled ? null : this.getDateTimeString(malfunctionRange.from);
    const malfunctionEnd: string | null = malfunctionRange.disabled ? null : this.getDateTimeString(malfunctionRange.to);

    return {
      topic: 'getAllMaintenanceNotifications',
      data: {
        language,
        filterOptions: {
          FreshDataFlag,
          // data filters
          ...(search && { SearchText: search }),
          ...(systemStatuses && { ConcatenatedActiveSystStsName: systemStatuses }),
          ...(workcenters && { MainWorkCenter: workcenters }),
          ...(locations && { FunctionalLocation: locations }),
          ...(notificationTypes && { NotificationType: notificationTypes }),
          ...(sapMachines && { TechnicalObject: sapMachines }),

          ...(timestampStart && { NotificationCreationDateStart: timestampStart }),
          ...(timestampEnd && { NotificationCreationDateEnd: timestampEnd }),
          ...(malfunctionStart && { MalfunctionStartDate: malfunctionStart }),
          ...(malfunctionEnd && { MalfunctionEndDate: malfunctionEnd }),
          // table filters
          ...this.createTableOrderOldFilter(meta),
          ...this.createTablePaginationDeprecatedFilter(meta)
        }
      }
    };
  }

  static createRequestForSapOrdersOperations(
    meta: MetaData,
    filters: SapOrdersFilter,
    freshDataFlag: boolean,
    language: string = 'en',
    machineNumber: string | null = null
  ): WebPubSubRequest<'getAllMaintenanceOrderOperations'> {

    const search: string|null = filters?.search || null;

    const systemStatuses: string[]|null = this.mapStatusItemForRequest(filters.systemStates);
    const userStates: string[]|null = this.mapStatusItemForRequest(filters.userStates);
    const workcenters: string[]|null = this.mapWorkcentersForRequest(filters.workcenters);
    const locations: string[]|null = this.mapLocationsForRequest(filters.locations);
    const orderTypes: string[]|null = this.mapOrderTypesForRequest(filters.orderTypes);
    const sapMachines: string[]|null = this.mapSapMachinesForRequest(filters.sapAsyncMachines, machineNumber);

    const orderRange: DateRange = convertToDateRange(filters.orderRange);
    const orderStart: string | null = orderRange.disabled ? null : this.getDateTimeString(orderRange.from);
    const orderEnd: string | null = orderRange.disabled ? null : this.getDateTimeString(orderRange.to);

    const filterOptions: GetAllMaintenanceOrderOperationsFilterOptionsDto = {
      freshDataFlag,
      // data filters
      ...(search && { searchText: search }),
      ...(systemStatuses && { systemStatusText: systemStatuses }),
      ...(userStates && { userStatusText: userStates }),
      ...(workcenters && { workCenter: workcenters }),
      ...(locations && { functionalLocation: locations }),
      ...(orderTypes && { maintenanceOrderType: orderTypes }),
      ...(sapMachines && { equipment: sapMachines }),
      ...(orderStart && { maintOrdBasicStartDateTime: orderStart }),
      ...(orderEnd && { maintOrdBasicEndDateTime: orderEnd }),
      // table filters
      ...this.createTableOrderFilter(meta),
      ...this.createTablePaginationFilter(meta)
    };

    return {
      topic: 'getAllMaintenanceOrderOperations',
      data: { language, filterOptions }
    };
  }

  static createRequestForGetAssignedToWorkcentersUsers(
    workCenters: SapWorkCenterDto[],
    language: string
  ): WebPubSubRequest<'getPersonsAssignedToWorkcenter'> {
    return {
      topic: 'getPersonsAssignedToWorkcenter',
      data: {
        language,
        filterOptions: SapUtils.buildPersonAssignedToWorkCenterFilterOptions(workCenters),
      },
    };
  }

  static mapWorkcenterAssignedPersonsModelToSapAssignedPerson(dtos: PersonAssignedToWorkcenterDto[]): SapAssignedPerson[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(this.mapWorkcenterAssignedPersonToSapAssignedPerson.bind(this));
  }

  static mapWorkcenterAssignedPersonToSapAssignedPerson(dto: PersonAssignedToWorkcenterDto): SapAssignedPerson {
    return {
      personId: dto.personId,
      firstName: dto.firstName,
      lastName: dto.lastName,
      workcenter: dto.workcenter,
      plant: dto.plant,
      capacityId: dto.capacityId
    }
  }
  /**
   * @deprecated, moved to SapTypesMapperUtils
   */
  static mapOperationAssignedPersonsModelToSapAssignedPerson(dtos: PersonAssignedToOperationDto[]): SapAssignedPerson[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(this.mapOperationAssignedPersonToSapAssignedPerson.bind(this));
  }
  /**
   * @deprecated, moved to SapTypesMapperUtils
   */
  static mapOperationAssignedPersonToSapAssignedPerson(dto: PersonAssignedToOperationDto): SapAssignedPerson {
    return {
      personId: dto.personId,
      firstName: dto.firstName,
      lastName: dto.lastName,
      activity: dto.activity,
      orderId: dto.orderId,
    }
  }

  static mapAssignedPersonsModelToSettingsSapUser(dtos: PersonAssignedToWorkcenterDto[]): SettingsSapUser[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(this.mapAssignedPersonToSapUser.bind(this));
  }

  static mapAssignedPersonToSapUser(dto: PersonAssignedToWorkcenterDto): SettingsSapUser {
    return {
      workcenter: dto.workcenter,
      id: dto.personId,
      lastName: dto.lastName,
      firstName: dto.firstName,
      plant: dto.plant,
    }
  }

  private static buildPersonAssignedToWorkCenterFilterOptions(workCentersIn: SapWorkCenterDto[] | SapWorkCenterDto): GetPersonsAssignedToWorkcenterFilterOptionsDto {
    if (!workCentersIn) {
      return { workCenters: [], plants: [] };
    }

    const workCentersArray: SapWorkCenterDto[] = Array.isArray(workCentersIn) ? workCentersIn : [workCentersIn];

    const workCenters: string[] = workCentersArray
      .filter((workCenter: SapWorkCenterDto) => workCenter?.code)
      .map((workCenter: SapWorkCenterDto) => workCenter.code);

    const plants: string[] = [...new Set(
      workCentersArray
        .filter((workCenter: SapWorkCenterDto) => workCenter?.plant)
        .map((workCenter: SapWorkCenterDto) => workCenter.plant)
    )];

    return { workCenters, plants };
  }


  static batchSapDescription(description: string): string[] {
    if (!StringUtils.isString(description) || description === '') {
      return [''];
    }
    /**
     * Regexp split text of following format
     * "28.02.2024 11:28:29 CET (DE9U7121) Description 03.02.2023 09:34:44 CET (DE9PMBDE) Another Description"
     * on two description -
     * 1: "28.02.2024 11:28:29 CET (DE9U7121) Description"
     * 2: "03.02.2023 09:34:44 CET (DE9PMBDE) Another Description"
     * Also in some cases when we may have duplication of date and user title, regexp will recognize this and count
     * "28.02.2024 11:28:29 CET (DE9U7121) 28.02.2024 11:28:29 CET (DE9U7121) Description"
     * like one description.
     * It may occur if we provide not a new text but all description instead
     */
    const regex: RegExp =
      /\b\d{2}\.\d{2}.\d{4} \d{2}:\d{2}:\d{2} CET \([A-Z0-9]+\)(?:\s+\d{2}\.\d{2}.\d{4} \d{2}:\d{2}:\d{2} CET \([A-Z0-9]+\))?(?:(?!\b\d{2}\.\d{2}.\d{4} \d{2}:\d{2}:\d{2} CET \([A-Z0-9]+\))[\s\S])*/g;
    const match: RegExpMatchArray | null = description.match(regex);

    return match ?? [description];
  }

  static formatSapUnformatted(text: string): string {
    if (!StringUtils.isString(text) || text === '') {
      return '';
    }

    const regex: RegExp = /(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2} CET \([A-Z0-9]+\)) (.+)/;
    const match: RegExpMatchArray | null = text.match(regex);

    if (match) {
      const dateTimePart: string = match[1];
      const descriptionPart: string = match[2];

      return `<div class="text-muted white-spaces-break">${dateTimePart}</div><p class="white-spaces-break">${descriptionPart}</p>`;
    }

    return `<p class="white-spaces-break">${text}</p>`;
  }

  /**
   * @param date as Date and optional
   * @param time as Date will be converted in string of type "/Date(1492041600000)/"
   * in case if time is not provide hours, minutes and seconds is going to be 0;
   */
  static dateToSapDate(date: Date, time?: Date): string | null {
    if (!(date instanceof Date)) {
      return null;
    }

    const year: number = date.getFullYear();
    const month: number = date.getMonth();
    const day: number = date.getDate();
    const hours: number = time?.getHours() || 0;
    const minutes: number = time?.getMinutes() || 0;
    const seconds: number = time?.getSeconds() || 0;

    const combinedDateTime: Date = new Date(year, month, day, hours, minutes, seconds);

    return `/Date(${combinedDateTime.getTime()})/`;
  }

  static calculateDefaultOrderType(data: OrderTypeCalculationData): SapOrderTypeDto | null {
    const [notification, orderTypes, notificationTypes] = data;
    const notificationType: string = notification.notificationType.code;
    const fullNotificationTypeData: NotificationType | undefined = notificationTypes?.find(
      (type: NotificationType) => type.NotificationType === notificationType
    );

    if (!fullNotificationTypeData) {
      return null;
    }

    const defaultCode: string = fullNotificationTypeData.DefaultOrderType;
    const fullDefaultOrderType: SapOrderTypeDto | undefined = orderTypes?.find(
      (type: SapOrderTypeDto) => type.code === defaultCode
    );

    return fullDefaultOrderType || null;
  }

  static getActivityTypesForOrder(data: EditOrderCalculationData): SapActivityTypeDto[] {
    const [order, orderTypes] = data;
    if (!order || ArrayUtils.isEmpty(orderTypes)) {
      return [];
    }

    return orderTypes.find((type) => type.code === order.orderType?.code)?.activityTypes || [];
  }

  /**
   * @param time as Date will be converted in string of type "PT15H51M04S"
   */
  static timeToSapTime(time: Date): string | null {
    if (!(time instanceof Date)) {
      return null;
    }

    const hours: number = time.getHours();
    const minutes: number = time.getMinutes();
    const seconds: number = time.getSeconds();
    const twoDigit: (n: number) => string = (n: number): string => `${n}`.padStart(2, '0');

    return `PT${twoDigit(hours)}H${twoDigit(minutes)}M${twoDigit(seconds)}S`;
  }

  private static getDateTimeString(date: Date | string): string {
    if (typeof date === 'string') {
      return date.slice(0, 19);
    }

    return date.toISOString().slice(0, 19);
  }

  private static mapStatusItemForRequest(data: SapUserStatusDto[] | SapSystemStatusDto[]): string[] | null {
    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: SapUserStatusDto | SapSystemStatusDto) => el.id);
  }

  private static mapWorkcentersForRequest(data: SapWorkCenterDto[]): string[] | null {
    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: SapWorkCenterDto) => el.code);
  }

  private static mapLocationsForRequest(data: FunctionalLocation[]): string[] | null {
    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: FunctionalLocation) => el.FunctionalLocation);
  }

  private static mapNotificationTypesForRequest(data: NotificationType[]): string[] | null {
    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: NotificationType) => el.NotificationType);
  }

  private static mapOrderTypesForRequest(data: SapOrderTypeDto[]): string[] | null {
    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: SapOrderTypeDto) => el.code);
  }

  private static mapSapMachinesForRequest(data: EquipmentOverviewResponseDto[], machineNumber?: string | null): string[] | null {
    if (machineNumber) {
      return [machineNumber];
    }

    if (ArrayUtils.isEmpty(data)) {
      return null;
    }

    return data.map((el: EquipmentOverviewResponseDto) => el.number);
  }
}
